import type React from 'react';
import CreatableSelect from 'react-select/creatable';
import ReactSelect, {
  components,
  type AriaLiveMessages,
  type ClassNamesConfig,
  type GroupBase,
  type InputProps,
  type MultiValue,
} from 'react-select';
import { FormField, fieldDescribedBy } from '~/components/form/FormField';

export interface ComboOption {
  value: string;
  label: string;
  /** Listed (its reason readable in the label) but not choosable. */
  disabled?: boolean;
}

interface ComboFieldBase {
  id: string;
  /** Omit when the caller renders its own <label htmlFor={id}>. */
  label?: string;
  className?: string;
  /** Accessible name when the visible label is shared by repeated fields (e.g. one per endpoint). */
  ariaLabel?: string;
  /**
   * An option whose value is "" is the blank choice (Automatic, Off, "All actions"). It is
   * selectable again after another value is picked; without one, an empty value shows `placeholder`.
   */
  options: ComboOption[];
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
  /** An outside element that also describes this field (its id), e.g. a notice that says why it is disabled. */
  describedBy?: string;
  testId?: string;
  /** The toolbar and footer height (h-9, level with a `sm` Button) instead of a form field's h-11. */
  compact?: boolean;
}

interface ComboSingleProps extends ComboFieldBase {
  isMulti?: false;
  value: string;
  onChange: (value: string) => void;
  /** Also accept a typed value (pick it with Enter or "Add <value>"). Default off: fixed options only. */
  creatable?: boolean;
  /** Creatable only: a typed value must match, or it is not offered. */
  pattern?: RegExp;
  /** Shown beside a typed value, and when the typed text is refused, e.g. "Type a duration like 45s". */
  formatHint?: string;
  /** Fixed-option mode only: type to filter. A creatable field is always searchable. */
  searchable?: boolean;
}

/** Several fixed options, always searchable; the menu stays open while picking. */
interface ComboMultiProps extends ComboFieldBase {
  isMulti: true;
  value: string[];
  onChange: (value: string[]) => void;
  /** A "Select all" row at the top of the menu: picks every choosable option, or clears them. */
  selectAll?: boolean;
}

export type ComboFieldProps = ComboSingleProps | ComboMultiProps;

const SELECT_ALL: ComboOption = { value: '__select_all__', label: 'Select all' };

/** Carried on react-select's `selectProps` to the module-level input below. */
interface ComboInputProps {
  comboDescribedBy?: string;
  /** Fixed options: the input takes focus and keys but never text. */
  comboFixed?: boolean;
}

/**
 * The real input for every mode. react-select's fixed-option mode swaps in an internal dummy
 * input that ignores `aria-describedby`, so the field's helper text and outside notices were
 * never read with it; a read-only real input keeps both. Module-level so it never remounts.
 */
function ComboInput(props: InputProps<ComboOption, boolean>) {
  const { comboDescribedBy, comboFixed } = props.selectProps as unknown as ComboInputProps;
  const describedBy =
    [props['aria-describedby'], comboDescribedBy].filter(Boolean).join(' ') || undefined;
  return (
    <components.Input
      {...props}
      aria-describedby={describedBy}
      readOnly={comboFixed || undefined}
      aria-readonly={comboFixed || undefined}
      inputMode={comboFixed ? 'none' : undefined}
    />
  );
}

/**
 * Spoken guidance for a fixed-option field. Its input is a searchable one made read-only (see
 * ComboInput), so react-select's default would tell a screen reader to "type to refine list".
 */
const FIXED_GUIDANCE: AriaLiveMessages<ComboOption, false, GroupBase<ComboOption>> = {
  guidance: ({ context, isInitialFocus, tabSelectsValue, 'aria-label': ariaLabel }) => {
    if (context === 'input')
      return isInitialFocus
        ? `${ariaLabel || 'Select'} is focused, press Down to open the menu`
        : '';
    if (context === 'menu')
      return `Use Up and Down to choose options, press Enter to select the currently focused option, press Escape to exit the menu${tabSelectsValue ? ', press Tab to select the option and exit the menu' : ''}.`;
    return '';
  },
};

const classNames: ClassNamesConfig<ComboOption, boolean> = {
  // field-control owns the box (height, border, focus ring) for every field; px-0 because
  // react-select's value container carries the inner padding.
  control: ({ isDisabled }) => `field-control !px-0 ${isDisabled ? 'opacity-60' : ''}`,
  valueContainer: () => 'px-4',
  singleValue: () => 'text-gray-800 dark:text-white/90',
  placeholder: () => 'text-gray-400 dark:text-white/30',
  input: ({ selectProps }) =>
    `text-gray-800 dark:text-white/90 ${(selectProps as unknown as ComboInputProps).comboFixed ? 'caret-transparent' : ''}`,
  indicatorsContainer: () => 'pr-2 text-gray-400',
  indicatorSeparator: () => 'hidden',
  // Rendered in a portal on <body> (see `shared`): inside a card or modal with overflow hidden
  // the menu was clipped. Above modals (z-50), level with the title tooltip (z-60).
  menuPortal: () => '!z-60',
  menu: () =>
    'mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900',
  option: ({ isFocused, isSelected, isDisabled }) =>
    `px-4 py-2 text-sm ${isDisabled ? 'cursor-not-allowed text-gray-400 dark:text-gray-500' : 'cursor-pointer'} ${isSelected ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400' : isFocused && !isDisabled ? 'bg-gray-100 dark:bg-white/5' : ''} ${isSelected || isDisabled ? '' : 'text-gray-700 dark:text-gray-300'}`,
  noOptionsMessage: () => 'px-4 py-2 text-sm text-gray-500 dark:text-gray-400',
  multiValue: () =>
    'my-0.5 mr-1 flex items-center rounded-md bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-white/90',
  multiValueLabel: () => 'px-2 py-0.5 text-sm',
  multiValueRemove: () =>
    'rounded-r-md px-1 text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-white/10',
};

const compactClassNames: ClassNamesConfig<ComboOption, boolean> = {
  ...classNames,
  control: ({ isDisabled }) => `field-control !h-9 !px-0 text-sm ${isDisabled ? 'opacity-60' : ''}`,
  valueContainer: () => 'px-3',
};

// A multi control grows with its selected chips instead of holding the single h-11.
const multiClassNames: ClassNamesConfig<ComboOption, boolean> = {
  ...classNames,
  control: ({ isDisabled }) =>
    `field-control !h-auto min-h-11 !px-0 ${isDisabled ? 'opacity-60' : ''}`,
  valueContainer: () => 'flex flex-wrap px-3 py-1',
};

export function ComboField(props: ComboFieldProps) {
  const {
    id,
    label,
    ariaLabel,
    options,
    placeholder,
    disabled,
    helperText,
    required,
    describedBy,
    testId,
    className,
    compact,
  } = props;
  const shared = {
    inputId: id,
    'aria-label': ariaLabel,
    unstyled: true,
    options,
    placeholder,
    isDisabled: disabled,
    isOptionDisabled: (option: ComboOption) => option.disabled === true,
    menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
    menuPosition: 'fixed' as const,
    components: { Input: ComboInput },
    comboDescribedBy: fieldDescribedBy(id, helperText, undefined, describedBy),
  };
  const frame = (control: React.ReactNode) => (
    <FormField
      id={id}
      label={label}
      required={required}
      helperText={helperText}
      testId={testId}
      className={className}
    >
      {control}
    </FormField>
  );

  if (props.isMulti) {
    const { value, onChange, selectAll } = props;
    const choosable = options.filter((option) => !option.disabled).map((option) => option.value);
    const allSelected = choosable.length > 0 && choosable.every((v) => value.includes(v));
    const selected = value.map(
      (v) => options.find((option) => option.value === v) ?? { value: v, label: v },
    );
    return frame(
      <ReactSelect<ComboOption, true>
        {...shared}
        isMulti
        classNames={multiClassNames}
        value={selected}
        options={selectAll ? [SELECT_ALL, ...options] : options}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable
        tabSelectsValue={false}
        isOptionSelected={(option, current) =>
          option.value === SELECT_ALL.value
            ? allSelected
            : current.some((chosen) => chosen.value === option.value)
        }
        onChange={(next: MultiValue<ComboOption>, meta) => {
          if (meta.option?.value === SELECT_ALL.value)
            onChange(meta.action === 'select-option' ? choosable : []);
          else onChange(next.map((option) => option.value));
        }}
      />,
    );
  }

  const { value, onChange, creatable = false, pattern, formatHint, searchable = false } = props;
  const selected =
    options.find((option) => option.value === value) ?? (value ? { value, label: value } : null);
  const single = {
    ...shared,
    classNames: (compact ? compactClassNames : classNames) as ClassNamesConfig<ComboOption, false>,
    value: selected,
    onChange: (option: ComboOption | null) => onChange(option?.value ?? ''),
  };
  return frame(
    creatable ? (
      <CreatableSelect
        {...single}
        createOptionPosition="first"
        isValidNewOption={(input) =>
          input.trim() !== '' && (pattern ? pattern.test(input.trim()) : true)
        }
        // A string label, so the live region reads "Add 45s"; the hint line is drawn by formatOptionLabel.
        getNewOptionData={(input) =>
          ({ value: input.trim(), label: `Add ${input.trim()}`, __isNew__: true }) as ComboOption
        }
        formatOptionLabel={(option, { context }) =>
          (option as ComboOption & { __isNew__?: boolean }).__isNew__ &&
          formatHint &&
          context === 'menu' ? (
            <>
              <span>{option.label}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">{formatHint}</span>
            </>
          ) : (
            option.label
          )
        }
        noOptionsMessage={() => formatHint ?? 'No options'}
      />
    ) : (
      <ReactSelect
        {...single}
        ariaLiveMessages={searchable ? undefined : FIXED_GUIDANCE}
        {...({ comboFixed: !searchable } as object)}
      />
    ),
  );
}
